let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
// screens/JobsOffersScreen.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  Image,
  FlatList,
  Animated,
  Dimensions,
  Switch,
  Platform,
  RefreshControl,
  ActivityIndicator,
  Alert,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';
import { useAuth } from '../../context/AuthContext';
import AsyncStorage from '@react-native-async-storage/async-storage';

import ApiClient from '../../services/apiClient';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width } = Dimensions.get('window');

export default function JobsOffersScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_jobsopportunitiesscreen`;
    // Uses shared fetchPublicJobs — single endpoint, is_active=true enforced
    require('./jobsShared').fetchPublicJobs({ limit: 50 }).then(jobs => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const { user, isAuthenticated } = useAuth();

  // States
  const [activeTab, setActiveTab] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    jobType: [],
    location: [],
    salary: [],
    experience: [],
    remote: false,
    smartContract: false,
  });
  const [favoriteJobs, setFavoriteJobs] = useState([]);
  const [showARPreview, setShowARPreview] = useState(false);
  const [selectedARJob, setSelectedARJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    hasMore: true,
  });

  // Animation values
  const filterHeight = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Load jobs from database
  const loadJobs = useCallback(async (page = 1, refresh = false) => {
    if (refresh) {
      setIsRefreshing(true);
      setError(null);
    } else if (page === 1) {
      setIsLoading(true);
      setError(null);
    }

    try {
      const params = {
        page,
        limit: pagination.limit,
        search: searchQuery.trim(),
        tab: activeTab,
        filters: selectedFilters,
        userId: user?.id,
      };

      // Simulate API call with timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 8000)
      );
      
      // Load from AsyncStorage first
      const savedJobs = await AsyncStorage.getItem(`professional_${_profUserId}_jobs`);
      let realJobs = [];
      
      if (savedJobs) {
        const jobsData = JSON.parse(savedJobs);
        realJobs = Array.isArray(jobsData) ? jobsData.filter(j => j.status === 'active') : [];
      }
      
      // Mock API response
      const apiPromise = new Promise((resolve) => {
        setTimeout(() => {
          // Use real jobs if available, otherwise generate mock
          const jobsToUse = realJobs.length > 0 ? realJobs : generateMockJobs(page, params);
          resolve({
            success: true,
            data: {
              jobs: jobsToUse,
              pagination: {
                page,
                limit: pagination.limit,
                total: jobsToUse.length,
                hasMore: false,
              },
              favoriteJobIds: user?.favoriteJobs || [],
            }
          });
        }, 500);
      });

      const response = await Promise.race([apiPromise, timeoutPromise]);

      if (response.success) {
        const newJobs = response.data.jobs;
        
        if (page === 1 || refresh) {
          setJobs(newJobs);
        } else {
          setJobs(prevJobs => [...prevJobs, ...newJobs]);
        }

        setPagination(response.data.pagination);
        setFavoriteJobs(response.data.favoriteJobIds);
      } else {
        throw new Error(response.message || 'Failed to load jobs');
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
      console.error('❌ Error details:', error.message);
      setError(error.message);
      
      // Fallback to mock data on error
      if (page === 1) {
        const fallbackJobs = generateMockJobs(1, {});
        setJobs(fallbackJobs);
        setPagination({ page: 1, limit: 10, hasMore: true });
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [searchQuery, activeTab, selectedFilters, user, pagination.limit]);

  // Generate mock jobs data
  const generateMockJobs = (page, params) => {
    const companies = [
      { name: 'Rolls-Royce', logo: 'https://via.placeholder.com/48x48/667eea/ffffff?text=RR', industry: 'Aerospace' },
      { name: 'Arup', logo: 'https://via.placeholder.com/48x48/4facfe/ffffff?text=AR', industry: 'Engineering' },
      { name: 'ARM Holdings', logo: 'https://via.placeholder.com/48x48/43e97b/ffffff?text=ARM', industry: 'Technology' },
      { name: 'Mott MacDonald', logo: 'https://via.placeholder.com/48x48/fa709a/ffffff?text=MM', industry: 'Consulting' },
      { name: 'BAE Systems', logo: 'https://via.placeholder.com/48x48/667eea/ffffff?text=BAE', industry: 'Defense' },
      { name: 'National Grid', logo: 'https://via.placeholder.com/48x48/4facfe/ffffff?text=NG', industry: 'Energy' },
      { name: 'Dyson', logo: 'https://via.placeholder.com/48x48/43e97b/ffffff?text=DY', industry: 'Technology' },
      { name: 'Jacobs', logo: 'https://via.placeholder.com/48x48/fa709a/ffffff?text=JA', industry: 'Engineering' },
    ];

    const jobTitles = [
      'Senior Software Engineer', 'Mechanical Engineer', 'Structural Engineer',
      'Embedded Systems Engineer', 'Civil Engineering Consultant', 'Aerospace Engineer',
      'Electrical Engineer', 'Robotics Engineer', 'Environmental Engineer', 'Data Scientist'
    ];

    const locations = ['London, UK', 'Manchester, UK', 'Birmingham, UK', 'Edinburgh, UK', 'Cambridge, UK', 'Bristol, UK'];
    const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Freelance'];
    const statuses = [null, 'Applied', 'Interview', 'Rejected'];

    return Array.from({ length: 10 }, (_, index) => {
      const jobIndex = (page - 1) * 10 + index;
      const company = companies[jobIndex % companies.length];
      const title = jobTitles[jobIndex % jobTitles.length];
      const location = locations[jobIndex % locations.length];
      const jobType = jobTypes[jobIndex % jobTypes.length];
      const status = statuses[jobIndex % statuses.length];
      
      return {
        id: `job-${jobIndex + 1}`,
        title,
        company: company.name,
        location,
        salary: `£${45 + (jobIndex % 6) * 10},000 - £${65 + (jobIndex % 6) * 10},000`,
        type: jobType,
        posted: `${Math.floor(Math.random() * 14) + 1} days ago`,
        logo: company.logo,
        matchScore: 70 + (jobIndex % 25),
        hasAR: jobIndex % 3 === 0,
        smartContract: jobIndex % 4 === 0,
        status,
        description: `Exciting opportunity to work as a ${title} at ${company.name}. Join our innovative team and contribute to cutting-edge projects in the ${company.industry} industry.`,
        skills: ['JavaScript', 'React', 'Node.js', 'Python'].slice(0, 2 + (jobIndex % 3)),
        industry: company.industry,
        experience: ['Entry Level', 'Mid Level', 'Senior Level'][jobIndex % 3],
        remote: jobIndex % 5 === 0,
        createdAt: new Date(Date.now() - (jobIndex * 24 * 60 * 60 * 1000)).toISOString(),
        updatedAt: new Date().toISOString(),
      };
    });
  };

  // Save/unsave job to database
  const toggleFavorite = async (jobId) => {
    if (!isAuthenticated) {
      Alert.alert(
        'Sign In Required',
        'Please sign in to save jobs.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Sign In', onPress: () => navigation.navigate('Login') },
        ]
      );
      return;
    }

    try {
      const isFavorite = favoriteJobs.includes(jobId);
      const endpoint = isFavorite ? '/api/jobs/unsave' : '/api/jobs/save';
      
      // Optimistic update
      if (isFavorite) {
        setFavoriteJobs(prev => prev.filter(id => id !== jobId));
      } else {
        setFavoriteJobs(prev => [...prev, jobId]);
      }

      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // In real implementation:
      // const response = await ApiClient.request(endpoint, {
      //   method: 'POST',
      //   body: { jobId, userId: user.id },
      // });
      
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert optimistic update on error
      if (favoriteJobs.includes(jobId)) {
        setFavoriteJobs(prev => prev.filter(id => id !== jobId));
      } else {
        setFavoriteJobs(prev => [...prev, jobId]);
      }
      Alert.alert('Error', 'Failed to update favorite status. Please try again.');
    }
  };

  // Apply for job
  const applyForJob = async (jobId) => {
    if (!isAuthenticated) {
      Alert.alert(
        'Sign In Required',
        'Please sign in to apply for jobs.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Sign In', onPress: () => navigation.navigate('Login') },
        ]
      );
      return;
    }

    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update job status locally
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId ? { ...job, status: 'Applied' } : job
        )
      );

      Alert.alert(
        'Application Submitted',
        'Your application has been submitted successfully!',
        [
          { text: 'View Applications', onPress: () => navigation.navigate('JobApplications') },
          { text: 'OK', style: 'default' },
        ]
      );
    } catch (error) {
      console.error('Error applying for job:', error);
      Alert.alert('Error', 'Failed to submit application. Please try again.');
    }
  };

  // Toggle filter visibility
  const toggleFilterSelection = () => {
    setFilterVisible(!filterVisible);
    Animated.timing(filterHeight, {
      toValue: filterVisible ? 0 : 1,
      duration: 300,
      useNativeDriver: false,
    }).start();
  };

  // Toggle filter selection
  const toggleFilter = (category, value) => {
    const currentFilters = { ...selectedFilters };

    if (category === 'remote' || category === 'smartContract') {
      currentFilters[category] = !currentFilters[category];
    } else {
      if (currentFilters[category].includes(value)) {
        currentFilters[category] = currentFilters[category].filter(
          item => item !== value
        );
      } else {
        currentFilters[category] = [...currentFilters[category], value];
      }
    }

    setSelectedFilters(currentFilters);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedFilters({
      jobType: [],
      location: [],
      salary: [],
      experience: [],
      remote: false,
      smartContract: false,
    });
  };

  // Apply filters and reload data
  const applyFilters = () => {
    toggleFilterSelection();
    setPagination(prev => ({ ...prev, page: 1, hasMore: true }));
    loadJobs(1);
  };

  // Handle search
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setPagination(prev => ({ ...prev, page: 1, hasMore: true }));
  }, []);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPagination(prev => ({ ...prev, page: 1, hasMore: true }));
  };

  // Load more jobs (pagination)
  const loadMoreJobs = () => {
    if (!isLoading && pagination.hasMore) {
      loadJobs(pagination.page + 1);
    }
  };

  // Refresh jobs
  const onRefresh = () => {
    setPagination(prev => ({ ...prev, page: 1, hasMore: true }));
    loadJobs(1, true);
  };

  // Show AR preview for a job
  const showARPreviewForJob = (job) => {
    setSelectedARJob(job);
    setShowARPreview(true);
  };

  // Initial load and search debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1, hasMore: true }));
      loadJobs(1);
    }, searchQuery ? 500 : 0);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Load jobs when tab changes
  useEffect(() => {
    loadJobs(1);
  }, [activeTab]);

  // Start animations
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // Render job card
  const renderJobCard = ({ item }) => (
    <Animated.View
      style={[
        styles.jobCard,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        }
      ]}
    >
      <TouchableOpacity
        onPress={() => navigation.navigate('JobDetails', { job: item })}
        activeOpacity={0.7}
      >
        <View style={styles.jobCardHeader}>
          <View style={styles.companyLogoContainer}>
            <Image 
              source={{ uri: (() => {
                try {
                  if (item.logo) return item.logo;
                  const companyName = item.company || item.department || 'Company';
                  const initials = String(companyName).substring(0, 2).toUpperCase();
                  return `https://via.placeholder.com/48x48/667eea/ffffff?text=${initials}`;
                } catch (e) {
                  return 'https://via.placeholder.com/48x48/667eea/ffffff?text=CO';
                }
              })() }} 
              style={styles.companyLogo} 
            />
            {item.smartContract && (
              <View style={styles.smartContractBadge}>
                <MaterialCommunityIcons name="shield-check" size={12} color="#43e97b" />
              </View>
            )}
          </View>

          <View style={styles.jobTitleContainer}>
            <Text style={styles.jobTitle} numberOfLines={2}>{item.title}</Text>
            <TouchableOpacity onPress={() => navigation.navigate('CompanyInfo', { company: item.company })}>
              <Text style={styles.companyName}>{item.company || item.department || 'Company'}</Text>
            </TouchableOpacity>
            <Text style={styles.industryText}>{item.industry}</Text>
          </View>

          <TouchableOpacity
            style={styles.favoriteButton}
            onPress={() => toggleFavorite(item.id)}
          >
            <MaterialCommunityIcons
              name={favoriteJobs.includes(item.id) ? 'heart' : 'heart-outline'}
              size={24}
              color={favoriteJobs.includes(item.id) ? '#ff6b6b' : '#64748b'}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.jobCardContent}>
          <View style={styles.jobMetaContainer}>
            <View style={styles.jobMetaChip}>
              <MaterialCommunityIcons name="map-marker" size={14} color="#667eea" />
              <Text style={styles.jobMetaText}>{item.location}</Text>
            </View>

            <View style={styles.jobMetaChip}>
              <MaterialCommunityIcons name="currency-gbp" size={14} color="#43e97b" />
              <Text style={styles.jobMetaText}>{item.salary || `${item.currency || '£'}${item.salaryMin || '0'} - ${item.currency || '£'}${item.salaryMax || '0'}`}</Text>
            </View>

            <View style={styles.jobMetaChip}>
              <MaterialCommunityIcons name="clock-outline" size={14} color="#4facfe" />
              <Text style={styles.jobMetaText}>{item.posted}</Text>
            </View>
          </View>

          <View style={styles.jobTypeContainer}>
            <Text style={styles.jobTypeText}>{item.type || item.jobType || 'Full-time'}</Text>
            {item.remote && (
              <View style={styles.remoteTag}>
                <MaterialCommunityIcons name="laptop" size={12} color="#fa709a" />
                <Text style={styles.remoteTagText}>Remote</Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.skillsContainer}>
          {(item.skills || item.requiredSkills || []).slice(0, 3).map((skill, index) => (
            <View key={index} style={styles.skillTag}>
              <Text style={styles.skillTagText}>{skill}</Text>
            </View>
          ))}
          {(item.skills || item.requiredSkills || []).length > 3 && (
            <Text style={styles.moreSkillsText}>+{(item.skills || item.requiredSkills || []).length - 3} more</Text>
          )}
        </View>

        <View style={styles.jobCardFooter}>
          <View style={styles.matchScoreContainer}>
            <Text style={styles.matchScoreLabel}>Match Score</Text>
            <View style={[
              styles.matchScoreCircle,
              { backgroundColor: item.matchScore >= 85 ? '#dcfce7' : item.matchScore >= 70 ? '#fef3c7' : '#fee2e2' }
            ]}>
              <Text style={[
                styles.matchScoreText,
                { color: item.matchScore >= 85 ? '#16a34a' : item.matchScore >= 70 ? '#d97706' : '#dc2626' }
              ]}>{item.matchScore}%</Text>
            </View>
          </View>

          <View style={styles.jobActions}>
            {item.hasAR && (
              <TouchableOpacity
                style={styles.arButton}
                onPress={() => showARPreviewForJob(item)}
              >
                <MaterialCommunityIcons name="augmented-reality" size={16} color="#667eea" />
                <Text style={styles.arButtonText}>AR View</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[
                styles.applyButton,
                item.status === 'Applied' && styles.appliedButton
              ]}
              onPress={() => item.status ? null : applyForJob(item.id)}
              disabled={!!item.status}
            >
              <MaterialCommunityIcons 
                name={item.status === 'Applied' ? 'check-circle' : 'send'} 
                size={16} 
                color="#fff" 
              />
              <Text style={styles.applyButtonText}>
                {item.status === 'Applied' ? 'Applied' : 'Apply'}
              </Text>
            </TouchableOpacity>
          </View>

          {item.status && item.status !== 'Applied' && (
            <View
              style={[
                styles.statusTag,
                item.status === 'Interview' ? styles.statusInterview : styles.statusRejected,
              ]}
            >
              <Text style={styles.statusText}>{item.status}</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    </Animated.View>
  );

  // Render filter section
  const renderFilterSection = () => (
    <Animated.View
      style={[
        styles.filterContainer,
        {
          maxHeight: filterHeight.interpolate({
            inputRange: [0, 1],
            outputRange: [0, 600],
          }),
          opacity: filterHeight,
        },
      ]}
    >
      <ScrollView style={styles.filterScrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.filterSection}>
          <Text style={styles.filterSectionTitle}>Job Type</Text>
          <View style={styles.filterOptions}>
            {['Full-time', 'Part-time', 'Contract', 'Freelance'].map(type => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.filterOption,
                  selectedFilters.jobType.includes(type) && styles.filterOptionSelected,
                ]}
                onPress={() => toggleFilter('jobType', type)}
              >
                <Text
                  style={[
                    styles.filterOptionText,
                    selectedFilters.jobType.includes(type) && styles.filterOptionTextSelected,
                  ]}
                >
                  {type}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.filterSection}>
          <Text style={styles.filterSectionTitle}>Location</Text>
          <View style={styles.filterOptions}>
            {['London', 'Manchester', 'Birmingham', 'Edinburgh', 'Cambridge', 'Bristol'].map(location => (
              <TouchableOpacity
                key={location}
                style={[
                  styles.filterOption,
                  selectedFilters.location.includes(location) && styles.filterOptionSelected,
                ]}
                onPress={() => toggleFilter('location', location)}
              >
                <Text
                  style={[
                    styles.filterOptionText,
                    selectedFilters.location.includes(location) && styles.filterOptionTextSelected,
                  ]}
                >
                  {location}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.filterSection}>
          <Text style={styles.filterSectionTitle}>Experience Level</Text>
          <View style={styles.filterOptions}>
            {['Entry Level', 'Mid Level', 'Senior Level'].map(level => (
              <TouchableOpacity
                key={level}
                style={[
                  styles.filterOption,
                  selectedFilters.experience.includes(level) && styles.filterOptionSelected,
                ]}
                onPress={() => toggleFilter('experience', level)}
              >
                <Text
                  style={[
                    styles.filterOptionText,
                    selectedFilters.experience.includes(level) && styles.filterOptionTextSelected,
                  ]}
                >
                  {level}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.filterSection}>
          <Text style={styles.filterSectionTitle}>Salary Range</Text>
          <View style={styles.filterOptions}>
            {['< £50k', '£50k - £70k', '£70k - £90k', '> £90k'].map(range => (
              <TouchableOpacity
                key={range}
                style={[
                  styles.filterOption,
                  selectedFilters.salary.includes(range) && styles.filterOptionSelected,
                ]}
                onPress={() => toggleFilter('salary', range)}
              >
                <Text
                  style={[
                    styles.filterOptionText,
                    selectedFilters.salary.includes(range) && styles.filterOptionTextSelected,
                  ]}
                >
                  {range}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.filterSection}>
          <Text style={styles.filterSectionTitle}>Work Options</Text>
          <View style={styles.switchFilterOptions}>
            <View style={styles.switchFilterOption}>
              <View style={styles.switchFilterLabelContainer}>
                <MaterialCommunityIcons name="laptop" size={20} color="#667eea" />
                <Text style={styles.switchFilterText}>Remote Work Available</Text>
              </View>
              <Switch
                value={selectedFilters.remote}
                onValueChange={() => toggleFilter('remote')}
                trackColor={{ false: '#e2e8f0', true: 'rgba(102, 126, 234, 0.3)' }}
                thumbColor={selectedFilters.remote ? '#667eea' : '#f4f4f5'}
              />
            </View>

            <View style={styles.switchFilterOption}>
              <View style={styles.switchFilterLabelContainer}>
                <MaterialCommunityIcons name="shield-check" size={20} color="#43e97b" />
                <Text style={styles.switchFilterText}>Smart Contract Payment</Text>
              </View>
              <Switch
                value={selectedFilters.smartContract}
                onValueChange={() => toggleFilter('smartContract')}
                trackColor={{ false: '#e2e8f0', true: 'rgba(67, 233, 123, 0.3)' }}
                thumbColor={selectedFilters.smartContract ? '#43e97b' : '#f4f4f5'}
              />
            </View>
          </View>
        </View>

        <View style={styles.filterActions}>
          <TouchableOpacity style={styles.clearFiltersButton} onPress={clearFilters}>
            <MaterialCommunityIcons name="refresh" size={16} color="#64748b" />
            <Text style={styles.clearFiltersButtonText}>Clear All</Text>
          </TouchableOpacity>

          <TouchableOpacity style={styles.applyFiltersButton} onPress={applyFilters}>
            <MaterialCommunityIcons name="check" size={16} color="#fff" />
            <Text style={styles.applyFiltersButtonText}>Apply Filters</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </Animated.View>
  );

  // Render AR preview modal
  const renderARPreviewModal = () =>
    showARPreview && selectedARJob && (
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <View style={styles.modalHeaderLeft}>
              <MaterialCommunityIcons name="augmented-reality" size={24} color="#667eea" />
              <Text style={styles.modalTitle}>AR Workplace Preview</Text>
            </View>
            <TouchableOpacity 
              style={styles.modalCloseButton}
              onPress={() => setShowARPreview(false)}
            >
              <MaterialCommunityIcons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>

          <View style={styles.arPreviewContainer}>
            <View style={styles.arPreviewImageContainer}>
              <View style={styles.arPreviewPlaceholder}>
                <MaterialCommunityIcons name="office-building" size={80} color="#667eea" />
                <Text style={styles.arPreviewCompanyName}>{selectedARJob.company}</Text>
                <Text style={styles.arPreviewJobTitle}>{selectedARJob.title}</Text>
              </View>
            </View>

            <Text style={styles.arPreviewDescription}>
              Experience the {selectedARJob.company} workplace in augmented reality. 
              See your potential workspace, team environment, and office facilities before applying.
            </Text>

            <View style={styles.arFeatures}>
              <View style={styles.arFeature}>
                <MaterialCommunityIcons name="desk" size={20} color="#4facfe" />
                <Text style={styles.arFeatureText}>Virtual Desk Tour</Text>
              </View>
              <View style={styles.arFeature}>
                <MaterialCommunityIcons name="account-group" size={20} color="#43e97b" />
                <Text style={styles.arFeatureText}>Meet Your Team</Text>
              </View>
              <View style={styles.arFeature}>
                <MaterialCommunityIcons name="map-marker" size={20} color="#fa709a" />
                <Text style={styles.arFeatureText}>Office Layout</Text>
              </View>
            </View>

            <TouchableOpacity style={styles.arLaunchButton}>
              <MaterialCommunityIcons name="play-circle" size={20} color="#fff" />
              <Text style={styles.arLaunchButtonText}>Launch AR Experience</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );

  // Render empty state
  const renderEmptyState = () => (
    <View style={styles.emptyStateContainer}>
      <View style={styles.emptyStateIcon}>
        <MaterialCommunityIcons name="briefcase-search" size={64} color="#94a3b8" />
      </View>
      <Text style={styles.emptyStateTitle}>No jobs found</Text>
      <Text style={styles.emptyStateDescription}>
        {searchQuery 
          ? `No jobs match "${searchQuery}". Try different keywords or adjust your filters.`
          : 'Try adjusting your filters or search criteria to find more job opportunities.'
        }
      </Text>
      <TouchableOpacity style={styles.emptyStateButton} onPress={clearFilters}>
        <MaterialCommunityIcons name="refresh" size={16} color="#fff" />
        <Text style={styles.emptyStateButtonText}>Clear Filters</Text>
      </TouchableOpacity>
    </View>
  );

  // Render loading state
  const renderLoadingState = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size={36} color="#667eea" />
      <Text style={styles.loadingText}>Loading job opportunities...</Text>
    </View>
  );

  // Render footer loading
  const renderFooterLoading = () => {
    if (!isLoading || pagination.page === 1) return null;
    
    return (
      <View style={styles.footerLoading}>
        <ActivityIndicator size={20} color="#667eea" />
        <Text style={styles.footerLoadingText}>Loading more jobs...</Text>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#ffffff" />
        </TouchableOpacity>
        
        <View style={styles.headerContent}>
          <Text style={styles.headerTitle}>Job Opportunities</Text>
          <Text style={styles.headerSubtitle}>
            {jobs.length} {jobs.length === 1 ? 'position' : 'positions'} available
          </Text>
        </View>

        <TouchableOpacity
          style={styles.notificationButton}
          onPress={() => navigation.navigate('Notifications')}
        >
          <MaterialCommunityIcons name="bell" size={24} color="#ffffff" />
          <View style={styles.notificationBadge}>
            <Text style={styles.notificationBadgeText}>3</Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <MaterialCommunityIcons
            name="magnify"
            size={20}
            color="#64748b"
            style={styles.searchIcon}
          />
          <TextInput
            style={styles.searchInput}
            placeholder="Search jobs, companies, skills..."
            placeholderTextColor="#94a3b8"
            value={searchQuery}
            onChangeText={handleSearch}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              style={styles.clearSearchButton}
              onPress={() => handleSearch('')}
            >
              <MaterialCommunityIcons name="close-circle" size={20} color="#64748b" />
            </TouchableOpacity>
          )}
        </View>

        <TouchableOpacity style={styles.filterButton} onPress={toggleFilterSelection}>
          <MaterialCommunityIcons name="tune" size={20} color="#667eea" />
          {(selectedFilters.jobType.length > 0 || 
            selectedFilters.location.length > 0 || 
            selectedFilters.salary.length > 0 ||
            selectedFilters.experience.length > 0 ||
            selectedFilters.remote ||
            selectedFilters.smartContract) && (
            <View style={styles.filterActiveBadge}>
              <Text style={styles.filterActiveBadgeText}>!</Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      {/* Error Banner */}
      {error && (
        <View style={styles.errorBanner}>
          <MaterialCommunityIcons name="alert-circle" size={16} color="#f59e0b" />
          <Text style={styles.errorBannerText}>Using cached data - {error}</Text>
          <TouchableOpacity onPress={() => loadJobs(1)}>
            <MaterialCommunityIcons name="refresh" size={16} color="#f59e0b" />
          </TouchableOpacity>
        </View>
      )}

      {/* Filter Section */}
      {renderFilterSection()}

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabsScrollContent}
        >
          {[
            { key: 'All', label: 'All Jobs', icon: 'briefcase-outline' },
            { key: 'Recommended', label: 'Recommended', icon: 'star-outline' },
            { key: 'Favorites', label: 'Saved', icon: 'heart-outline' },
            { key: 'Applied', label: 'Applied', icon: 'send-outline' },
          ].map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeTab === tab.key && styles.activeTab]}
              onPress={() => handleTabChange(tab.key)}
            >
              <MaterialCommunityIcons 
                name={tab.icon} 
                size={16} 
                color={activeTab === tab.key ? '#fff' : '#64748b'} 
              />
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.key && styles.activeTabText,
                ]}
              >
                {tab.label}
              </Text>
              {tab.key === 'Recommended' && (
                <View style={styles.aiRecommendedBadge}>
                  <MaterialCommunityIcons name="flash" size={10} color="#fff" />
                </View>
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Job List */}
      <View style={styles.jobListContainer}>
        {isLoading && pagination.page === 1 ? (
          renderLoadingState()
        ) : jobs.length > 0 ? (
          <FlatList
            data={jobs}
            renderItem={renderJobCard}
            keyExtractor={item => item.id}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.jobListContent}
            refreshControl={
              <RefreshControl
                refreshing={isRefreshing}
                onRefresh={onRefresh}
                colors={['#667eea']}
                tintColor="#667eea"
              />
            }
            onEndReached={loadMoreJobs}
            onEndReachedThreshold={0.1}
            ListFooterComponent={renderFooterLoading}
          />
        ) : (
          renderEmptyState()
        )}
      </View>

      {/* AR Preview Modal */}
      {renderARPreviewModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  headerContent: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  notificationButton: {
    position: 'relative',
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  notificationBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: '#ff6b6b',
    borderRadius: 8,
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationBadgeText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#fff',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 12,
    paddingHorizontal: 16,
    marginRight: 12,
    height: 48,
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
  },
  clearSearchButton: {
    padding: 4,
  },
  filterButton: {
    width: 48,
    height: 48,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 12,
    position: 'relative',
  },
  filterActiveBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: '#ff6b6b',
    borderRadius: 6,
    width: 12,
    height: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterActiveBadgeText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#fff',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef3c7',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f59e0b',
  },
  errorBannerText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#92400e',
    flex: 1,
  },
  filterContainer: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    overflow: 'hidden',
  },
  filterScrollView: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  filterSection: {
    marginBottom: 20,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -4,
  },
  filterOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    margin: 4,
    backgroundColor: '#fff',
  },
  filterOptionSelected: {
    backgroundColor: '#667eea',
    borderColor: '#667eea',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  filterOptionTextSelected: {
    color: '#fff',
  },
  switchFilterOptions: {
    marginVertical: 8,
  },
  switchFilterOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
  },
  switchFilterLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  switchFilterText: {
    fontSize: 16,
    color: '#0f172a',
    marginLeft: 12,
  },
  filterActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
  },
  clearFiltersButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  clearFiltersButtonText: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
    marginLeft: 8,
  },
  applyFiltersButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  applyFiltersButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
    marginLeft: 8,
  },
  tabsContainer: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tabsScrollContent: {
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginHorizontal: 4,
    borderRadius: 20,
    position: 'relative',
  },
  activeTab: {
    backgroundColor: '#667eea',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748b',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#fff',
  },
  aiRecommendedBadge: {
    position: 'absolute',
    top: 6,
    right: 6,
    backgroundColor: '#ffd700',
    borderRadius: 8,
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  jobListContainer: {
    flex: 1,
  },
  jobListContent: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748b',
  },
  footerLoading: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
  },
  footerLoadingText: {
    marginLeft: 12,
    fontSize: 14,
    color: '#64748b',
  },
  jobCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    marginBottom: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  jobCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  companyLogoContainer: {
    position: 'relative',
    marginRight: 16,
  },
  companyLogo: {
    width: 56,
    height: 56,
    borderRadius: 14,
    backgroundColor: '#f1f5f9',
  },
  smartContractBadge: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 2,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  jobTitleContainer: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 4,
    lineHeight: 24,
  },
  companyName: {
    fontSize: 16,
    color: '#667eea',
    fontWeight: '500',
    marginBottom: 4,
  },
  industryText: {
    fontSize: 14,
    color: '#64748b',
  },
  favoriteButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f8fafc',
  },
  jobCardContent: {
    marginBottom: 16,
  },
  jobMetaContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  jobMetaChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 8,
  },
  jobMetaText: {
    fontSize: 14,
    color: '#64748b',
    marginLeft: 6,
    fontWeight: '500',
  },
  jobTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  jobTypeText: {
    fontSize: 14,
    color: '#667eea',
    fontWeight: '600',
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  remoteTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(250, 112, 154, 0.1)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginLeft: 8,
  },
  remoteTagText: {
    fontSize: 12,
    color: '#fa709a',
    fontWeight: '500',
    marginLeft: 4,
  },
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  skillTag: {
    backgroundColor: '#f1f5f9',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginRight: 6,
    marginBottom: 6,
  },
  skillTagText: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '500',
  },
  moreSkillsText: {
    fontSize: 12,
    color: '#94a3b8',
    alignSelf: 'center',
    marginLeft: 4,
  },
  jobCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#f1f5f9',
    position: 'relative',
  },
  matchScoreContainer: {
    alignItems: 'center',
  },
  matchScoreLabel: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 4,
  },
  matchScoreCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  matchScoreText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  jobActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  arButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginRight: 8,
  },
  arButtonText: {
    fontSize: 12,
    color: '#667eea',
    fontWeight: '500',
    marginLeft: 4,
  },
  applyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  appliedButton: {
    backgroundColor: '#43e97b',
  },
  applyButtonText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600',
    marginLeft: 4,
  },
  statusTag: {
    position: 'absolute',
    top: -8,
    right: 0,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  statusInterview: {
    backgroundColor: '#fef3c7',
  },
  statusRejected: {
    backgroundColor: '#fee2e2',
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#d97706',
  },
  emptyStateContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyStateIcon: {
    marginBottom: 20,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 12,
  },
  emptyStateDescription: {
    fontSize: 16,
    color: '#64748b',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  emptyStateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
  },
  emptyStateButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
    marginLeft: 8,
  },
  modalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    width: '90%',
    maxWidth: 400,
    backgroundColor: '#fff',
    borderRadius: 20,
    overflow: 'hidden',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modalHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginLeft: 12,
  },
  modalCloseButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f8fafc',
  },
  arPreviewContainer: {
    padding: 20,
    alignItems: 'center',
  },
  arPreviewImageContainer: {
    width: '100%',
    height: 200,
    marginBottom: 20,
    borderRadius: 16,
    overflow: 'hidden',
  },
  arPreviewPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  arPreviewCompanyName: {
    fontSize: 20,
    fontWeight: '600',
    color: '#667eea',
    marginTop: 12,
  },
  arPreviewJobTitle: {
    fontSize: 16,
    color: '#64748b',
    marginTop: 4,
  },
  arPreviewDescription: {
    fontSize: 16,
    color: '#374151',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 22,
  },
  arFeatures: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
    marginBottom: 24,
  },
  arFeature: {
    alignItems: 'center',
  },
  arFeatureText: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 8,
    textAlign: 'center',
  },
  arLaunchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    width: '100%',
  },
  arLaunchButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginLeft: 8,
  },
});